<template>
  <!-- 별첨 2 「요금·청구·정산」 본문 — 작성 화면과 피계약자 문서가 같이 쓴다 -->
  <div
    v-if="form.annex2Mode !== 'text'"
    :class="{ 'sc-annex2-locked': locked }"
    :inert="locked ? '' : undefined"
  >
    <h3>1. 선택한 서비스의 이용요금 (미선택 서비스는 “해당 없음” 표시)</h3>
    <p class="sc__field">
      임금관리:
      <span class="sc__opt">
        <ScMark :on="form.wageFee === 'none' || form.wageSelect === 'unselected'" aria-label="임금관리 해당 없음" @pick="toggleWageNone" /> 해당 없음(미선택)
      </span>
      <span class="sc__opt">
        <ScMark :on="form.wageFee === 'flat'" aria-label="임금관리 월정액" @pick="pickWageFee('flat')" /> 월정액
        <ScEditText v-model="form.wageFlat" :width="8" numeric :disabled="form.wageFee !== 'flat'" />원
      </span>
      <span class="sc__opt">
        <ScMark :on="form.wageFee === 'headcount'" aria-label="임금관리 인원연동" @pick="pickWageFee('headcount')" /> 인원연동(기본
        <ScEditText v-model="form.wageBaseHeadcount" :width="4" numeric :disabled="form.wageFee !== 'headcount'" />명 포함, 초과 1명당
        <ScEditText v-model="form.wageExtraPerPerson" :width="8" numeric :disabled="form.wageFee !== 'headcount'" />원)
      </span>
      <span class="sc__opt">
        <ScMark :on="form.wageFee === 'custom'" aria-label="임금관리 별도 산식" @pick="pickWageFee('custom')" /> 별도 산식
        <ScEditText v-model="form.wageCustomFormula" :width="8" :disabled="form.wageFee !== 'custom'" />
      </span>
    </p>
    <p class="sc__field">
      재무회계:
      <span class="sc__opt">
        <ScMark :on="form.acctFee === 'none' || form.acctSelect === 'unselected'" aria-label="재무회계 해당 없음" @pick="toggleAcctNone" /> 해당 없음(미선택)
      </span>
      <span class="sc__opt">
        <ScMark :on="form.acctFee === 'flat'" aria-label="재무회계 월정액" @pick="pickAcctFee('flat')" /> 월정액
        <ScEditText v-model="form.acctFlat" :width="8" numeric :disabled="form.acctFee !== 'flat'" />원
      </span>
      <span class="sc__opt">
        <ScMark :on="form.acctFee === 'volume'" aria-label="재무회계 업무량연동" @pick="pickAcctFee('volume')" /> 업무량연동
        <ScEditText v-model="form.acctVolume" :width="8" :disabled="form.acctFee !== 'volume'" />
      </span>
      <span class="sc__opt">
        <ScMark :on="form.acctFee === 'custom'" aria-label="재무회계 별도 산식" @pick="pickAcctFee('custom')" /> 별도 산식
        <ScEditText v-model="form.acctCustomFormula" :width="8" :disabled="form.acctFee !== 'custom'" />
      </span>
    </p>
    <p class="sc__field">
      월 이용료 합계(선택·개시된 서비스만)
      <ScEditText v-model="form.monthlyTotal" numeric />원
      <ScMark :on="form.vatMode === 'included'" aria-label="부가가치세 포함" @pick="form.vatMode = 'included'" /> 부가가치세 포함
      <ScMark :on="form.vatMode === 'separate'" aria-label="부가가치세 별도" @pick="form.vatMode = 'separate'" /> 부가가치세 별도
    </p>
    <p class="sc__field">
      ※ 별첨 1에서 미선택한 서비스의 금액란·일정란은 비워 두고 “해당 없음”으로 표시합니다.
      이미 인쇄된 요금항목이 있어도 별첨 1의 서비스 선택을 대신하지 않습니다.
    </p>
    <p class="sc__field">
      적용 인원·업무량 산정 기준일 [매월
      <ScEditText v-model="form.headcountDay" :width="4" numeric />일] / 기준 정의
      <ScEditText v-model="form.headcountDef" />
    </p>
    <h3>2. 할인·추가업무·청구</h3>
    <p class="sc__field">
      할인/프로모션 조건 <ScEditText v-model="form.discount" :width="20" />
      / 할인 적용기간 <ScEditText v-model="form.discountPeriod" :width="10" />
    </p>
    <p class="sc__field">
      청구 기준
      <ScMark :on="form.billingTiming === 'prepaid'" aria-label="선불" @pick="form.billingTiming = 'prepaid'" /> 선불
      <ScMark :on="form.billingTiming === 'postpaid'" aria-label="후불" @pick="form.billingTiming = 'postpaid'" /> 후불
      / 청구일 [매월 <ScEditText v-model="form.billDay" :width="4" numeric />일]
      / 납부기한 [매월 <ScEditText v-model="form.dueDay" :width="4" numeric />일]
      / 결제방법 <ScEditText v-model="form.payMethod" :width="8" />
    </p>
    <p class="sc__field">
      납부계좌(필요 시) [은행·계좌번호·예금주:
      <ScEditText v-model="form.bankAccount" :width="20" />]
    </p>
    <p class="sc__field">
      월중 개시·종료 정산 방식
      <ScMark :on="form.proration === 'daily'" aria-label="일할" @pick="form.proration = 'daily'" /> 일할
      <ScMark :on="form.proration === 'monthly'" aria-label="월정액" @pick="form.proration = 'monthly'" /> 월정액
      <ScMark :on="form.proration === 'other'" aria-label="기타" @pick="form.proration = 'other'" /> 기타
      <ScEditText v-model="form.prorationOther" :width="10" :disabled="form.proration !== 'other'" />
    </p>
    <p class="sc__field">
      추가 인원·추가업무 청구기준
      <ScEditText v-model="form.extraFeeRules" :width="20" />
    </p>
    <p class="sc__field">
      추가업무는 사전 견적 및 갑의 승인에 한하여 청구하며, 승인하지 않은 비용은 청구하지 않는다.
    </p>
    <h3>3. 연체·환불·변경</h3>
    <p class="sc__field">
      연체 시 지연손해금 조건(적법한 범위)
      <ScEditText v-model="form.lateFee" :width="16" />
      / 미기재 시 법정 기준 적용
    </p>
    <p class="sc__field">
      선납액·잔여서비스분 정산 및 환불 방식
      <ScEditText v-model="form.refundPolicy" :width="16" />
    </p>
    <p class="sc__field">
      요금 또는 중요 조건 변경은 본계약 제10조에 따라 사전 안내 후 명시적 합의로 확정한다.
    </p>
    <p class="sc__field">
      요금 적용 개시연월: 임금관리(선택 시)
      <ScEditDate
        :y="form.wageFeeStartYear || form.wageStartYear"
        :m="form.wageFeeStartMonth || form.wageStartMonth"
        type="ym"
        @update:y="form.wageFeeStartYear = $event"
        @update:m="form.wageFeeStartMonth = $event"
      />,
      재무회계(선택 시)
      <ScEditDate
        :y="form.acctFeeStartYear || form.acctStartYear"
        :m="form.acctFeeStartMonth || form.acctStartMonth"
        type="ym"
        @update:y="form.acctFeeStartYear = $event"
        @update:m="form.acctFeeStartMonth = $event"
      />
      / 합의일 <ScEditDate v-model:y="form.feeAgreedYear" v-model:m="form.feeAgreedMonth" v-model:d="form.feeAgreedDay" />
    </p>
  </div>
  <div v-else-if="locked" class="sc-annex2-read">{{ form.annex2Text || '별첨 2에 적힌 내용이 없습니다.' }}</div>
  <textarea
    v-else
    v-model="form.annex2Text"
    class="sc__annex2-text"
    rows="12"
    aria-label="별첨 2 문구"
    placeholder="요금, 청구, 정산 등 이 계약에 적용할 내용을 적습니다"
  />
</template>

<script setup lang="ts">
import ScMark from '@/components/contract/ScMark.vue'
import ScEditText from '@/components/contract/ScEditText.vue'
import ScEditDate from '@/components/contract/ScEditDate.vue'
import type { ServiceContractForm } from '@/composables/useServiceContractForm'

/* form은 부모가 내려준 반응형 계약서 모델 — 작성 화면·문서 화면이 함께 쓴다 */
const props = withDefaults(defineProps<{
  form: ServiceContractForm
  /** true면 피계약자 문서 — 쓰여진 내용만 보여 주고 고치지 못한다 */
  locked?: boolean
}>(), { locked: false })

/* 요금을 고르면 그 서비스는 「선택」이 된다 */
function pickWageFee(v: 'flat' | 'headcount' | 'custom') {
  props.form.wageSelect = 'selected'
  props.form.wageFee = v
}
function pickAcctFee(v: 'flat' | 'volume' | 'custom') {
  props.form.acctSelect = 'selected'
  props.form.acctFee = v
}
function toggleWageNone() {
  props.form.wageSelect = props.form.wageSelect === 'unselected' ? 'selected' : 'unselected'
}
function toggleAcctNone() {
  props.form.acctSelect = props.form.acctSelect === 'unselected' ? 'selected' : 'unselected'
}
</script>
